import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Share2 } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { api, errMessage } from "../lib/api";

// Family recap sharing: the family OWNER grants per-member, per-child recap
// visibility. Revocation applies instantly. Members can also get the weekly
// email (one-click unsubscribe); owners can preview it exactly as sent.
export const RecapSharingPanel = () => {
  const [data, setData] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);

  const load = useCallback(() => {
    api.get("/recap/shares").then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, []);
  useEffect(load, [load]);

  const previewEmail = async (memberId) => {
    try {
      const { data: p } = await api.get(`/recap/member-email-preview/${memberId}`);
      setEmailPreview(p);
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  if (!data || data.members.length === 0 || data.children.length === 0) return null;

  const shareFor = (memberId, childId) =>
    data.shares.find((s) => s.member_user_id === memberId && s.child_id === childId);

  const toggle = async (memberId, childId) => {
    const existing = shareFor(memberId, childId);
    try {
      if (existing) {
        await api.delete(`/recap/shares/${existing.share_id}`);
        toast.success("Recap sharing stopped for this child");
      } else {
        await api.post("/recap/shares", { member_user_id: memberId, child_id: childId });
        toast.success("Recap sharing granted");
      }
      load();
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="recap-sharing-panel">
      <div className="flex items-center gap-2">
        <Share2 className="h-4 w-4 text-secondary" aria-hidden="true" />
        <p className="text-sm font-bold">Weekly recap sharing</p>
      </div>
      <p className="text-xs text-muted-foreground">
        Choose which children each family member can see in their weekly recap preview.
        Only participation counts and story titles are shared — never chats, recordings or
        professional notes. You can switch any of these off at any time, effective immediately.
      </p>
      <div className="space-y-3">
        {data.members.map((m) => (
          <div key={m.user_id} className="rounded-xl bg-muted/40 p-3 space-y-2" data-testid={`recap-share-member-${m.user_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm font-semibold">{m.name} <span className="text-xs text-muted-foreground">({m.email})</span></p>
              <Button variant="outline" size="sm" className="rounded-lg h-8 gap-1.5"
                onClick={() => previewEmail(m.user_id)} data-testid={`recap-email-preview-${m.user_id}`}>
                <Mail className="h-3.5 w-3.5" aria-hidden="true" /> Preview email
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {data.children.map((c) => (
                <label key={c.child_id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={Boolean(shareFor(m.user_id, c.child_id))}
                    onCheckedChange={() => toggle(m.user_id, c.child_id)}
                    data-testid={`recap-share-toggle-${m.user_id}-${c.child_id}`} />
                  {c.display_name}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="recap-share-email-note">
        Family members can also receive these as a weekly email (with one-click unsubscribe).
        Use "Preview email" to see exactly what they'll get.
      </p>

      <Dialog open={Boolean(emailPreview)} onOpenChange={(v) => !v && setEmailPreview(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="recap-email-preview-dialog">
          <DialogHeader>
            <DialogTitle>Weekly email preview — {emailPreview?.member_name}</DialogTitle>
            <DialogDescription>
              {emailPreview?.children_count > 0
                ? `This is exactly what ${emailPreview?.member_name} will receive (${emailPreview.children_count} child${emailPreview.children_count > 1 ? "ren" : ""} shared). Emails are ${emailPreview?.email_enabled ? "ON" : "OFF"} for them.`
                : "Nothing is shared with this member yet — switch on a child above and the email will include them."}
            </DialogDescription>
          </DialogHeader>
          {emailPreview && (
            <iframe title="Email preview" srcDoc={emailPreview.html} sandbox=""
              className="w-full h-[420px] rounded-xl border border-border bg-white" data-testid="recap-email-preview-frame" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
